import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
  HiOutlineXMark,
} from "react-icons/hi2";

import "./ToastProvider.css";

/**
 * CONTEXT
 */
const ToastContext = createContext(null);

/**
 * PROVIDER
 */
export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  /**
   * REMOVE TOAST
   */
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * ADD TOAST
   */
  const showToast = useCallback(
    ({ title, message, type = "success", duration = 4000 }) => {
      const id = Date.now();

      const toast = {
        id,
        title,
        message,
        type,
      };

      setToasts((prev) => [...prev, toast]);

      /**
       * AUTO REMOVE
       */
      setTimeout(() => {
        removeToast(id);
      }, duration);
    },
    [removeToast],
  );

  /**
   * CONTEXT VALUE
   */
  const value = useMemo(
    () => ({
      showToast,
    }),
    [showToast],
  );

  /**
   * ICON
   */
  const getIcon = (type) => {
    switch (type) {
      case "success":
        return <HiOutlineCheckCircle />;

      case "error":
        return <HiOutlineExclamationCircle />;

      case "warning":
        return <HiOutlineExclamationCircle />;

      default:
        return <HiOutlineInformationCircle />;
    }
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* TOAST CONTAINER */}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.type}`}>
            {/* ICON */}
            <div className="toast__icon">{getIcon(toast.type)}</div>

            {/* CONTENT */}
            <div className="toast__content">
              {toast.title && <h4>{toast.title}</h4>}

              {toast.message && <p>{toast.message}</p>}
            </div>

            {/* CLOSE */}
            <button
              className="toast__close"
              onClick={() => removeToast(toast.id)}
            >
              <HiOutlineXMark />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * CONTEXT HOOK
 */
export const useToastContext = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToastContext must be used inside ToastProvider");
  }

  return context;
};
